import {
    PlatformDetails,
    GridSession,
    AppLaunchMode,
    ConnectionInfo,
    StreamParam,
    SessionState,
    ServerInfo,
    Usage,
    StreamInfo,
    Log,
    AddPlatformTelemetry,
    GetHexString,
    IsiOSVersionAtLeast,
    PackageVersion,
    IsWebOS,
    IsiOSVersion,
    customFetch,
    FetchResponse,
    IsiDevice
} from "./dependencies";
import {
    StartSessionResultEvent,
    StreamingTerminatedEvent,
    defaultInputConfigFlags,
    EVENTS,
    RNotificationCode
} from "./interfaces";
import {
    CustomMessage,
    DynamicStreamingMode,
    HidType,
    InputType,
    MicState,
    NskStreamClient,
    NskStreamClientDelegate,
    StreamClientInitParameters,
    StreamStartParameters,
    StreamingState,
    VirtualInputController,
    StreamStartData,
    DownstreamVideoSettings,
    StreamStopData,
    StreamStopReason,
    VideoCodecType,
    MediaTrackKind
} from "./nskinterfaces";
import { RErrorCode } from "./rerrorcode";
import { StreamClient } from "./streamclient";
import { IStreamCallbacks, LowAudioVolumeType } from "./rinterfaces";
import { GamepadHandler } from "./input/gamepadhandler";
import { Zoneless } from "./interfaces";
import { MicCapturer } from "./miccapturer";
import { GamepadTester } from "./debug/gamepadtester";
import { AudioRecorder } from "./debug/audiorecorder";
import { SleepDetector } from "./util/sleepdetector";
import { RagnarokSettings } from "./util/settings";
import {
    CapabilitiesHelper,
    IsMediaCapabilitiesSupported,
    ConvertCapabilityToNumber
} from "./util/devicecapabilities";
import {
    CanResume,
    ShouldRunConnectivityTest,
    ConvertErrorOnConnectivityTest,
    ConvertErrorOnSleep
} from "./util/utils";
import { Connectivity } from "./telemetry/telemetryinterfaces";
import { StreamKitTelemetryHandler } from "./telemetry/telemetryhandler";

const LOGTAG = "nskstreamclient";

const IOS_CONTEXT_PROP = "ios15AudioContext";
const IOS_STREAM_PROP = "ios15AudioStream";

performance.mark("GfnNskBegin");

declare interface LGEventDetails {
    idle: boolean;
}

declare interface LGSystemIdleEvent extends Event {
    detail: LGEventDetails;
}

interface ConnectivityStatus {
    errorCode: number;
    connectivity: string;
}

interface ValidatedStreamParams {
    streamParams: StreamStartParameters;
    audioElement?: HTMLAudioElement;
    videoElements: HTMLVideoElement[];
}

/**
 * Implementation class of the public NskStreamClient interface.
 * @todo Split responsibilities between this and StreamClient. Can gamepad and mic be owned by StreamClient, since only used in that context?
 */
export class NskStreamClientImpl
    implements NskStreamClient, IStreamCallbacks, VirtualInputController
{
    private streamClient?: StreamClient;
    private stutterIndicatorDisplayed: boolean = false;
    private debugStatsDisplayed: boolean = false;
    private gamepadHandler: GamepadHandler;
    private micCapturer: MicCapturer;
    private gamepadTester: GamepadTester = new GamepadTester();
    private audioRecorder: AudioRecorder = new AudioRecorder();
    private streamingState: StreamingState = StreamingState.NONE;
    private startTime: number = 0;
    private isResume: boolean = false;
    private sleepDetector: SleepDetector;
    private exitErrorCodeForUnload?: number;
    private systemIdleId: number = 0;
    private visibilityHiddenTime: number = 0;
    private deviceChangeCount: number = 0;
    private sessionId: string = "";
    private subSessionId: string = "";
    private zoneAddress: string = "";
    private zoneName: string = "";
    private videoElement?: HTMLVideoElement;
    private audioElement?: HTMLAudioElement;
    private micRecording: boolean = false;
    private appId: number = 0;

    /**
     * @param platformDetails - PlatformDetails obtained from @see getPlatformDetails
     * @param delegate - NskStreamClientDelegate to receive all the notifications.
     * @param initParams - Initialization parameters.
     */
    constructor(
        private platformDetails: PlatformDetails,
        private delegate: NskStreamClientDelegate,
        private initParams: StreamClientInitParameters
    ) {
        // todo: unify zoneless usage
        const zoneless = (window as any).zoneless as Zoneless;
        this.gamepadHandler = new GamepadHandler(this.platformDetails, zoneless);
        this.micCapturer = new MicCapturer(this.platformDetails, StreamKitTelemetryHandler);
        this.sleepDetector = new SleepDetector(StreamKitTelemetryHandler);

        if (RagnarokSettings.gamepadTesterEnabled) {
            this.gamepadHandler.addGamepadDataHandler(this.gamepadTester);
            this.gamepadHandler.enableUserInput();
        }

        // Play out any cached Platform telemetry
        AddPlatformTelemetry(StreamKitTelemetryHandler);
        // Send telemetry that won't change between sessions
        this.collectPerformanceTimingTelemetry();
        this.collectMediaCapabilitiesTelemetry();
        if (RagnarokSettings.ragnarokConfig.sendNonEssentialMetricEvents) {
            StreamKitTelemetryHandler.emitMetricEvent(
                "PlatformDetailsExecutionTime",
                "",
                this.platformDetails.totalTime,
                0,
                0,
                0
            );
        }

        Log.i("{0a9be04}", "{2569364}", PackageVersion);
        Log.d("{0a9be04}", "{e85ad66}", navigator.userAgent);
    }

    // NskStreamClient implementation
    public startStreaming(params: StreamStartParameters): RErrorCode {
        if (
            this.streamingState !== StreamingState.STOPPED &&
            this.streamingState !== StreamingState.NONE
        ) {
            Log.w("{0a9be04}", "{60a7ff5}");
            StreamKitTelemetryHandler.emitDebugEvent(
                "SessionSetupInProgressError",
                params.resume ? "resume" : "start"
            );
            return RErrorCode.InvalidOperation;
        }

        const validatedParams = this.updateStreamParams(params);
        params = validatedParams.streamParams;
        //TODO: handle more than one video element/stream
        this.videoElement = validatedParams.videoElements[0];
        this.audioElement = validatedParams.audioElement;
        if (!this.videoElement) {
            return RErrorCode.InvalidVideoElement;
        }
        if (!this.audioElement) {
            return RErrorCode.InvalidAudioElement;
        }

        this.isResume = params.resume ?? false;
        this.sessionId = params.sessionId;
        this.subSessionId = params.subSessionId ?? "";
        this.zoneAddress = params.zoneAddress ?? "";
        this.zoneName = params.statTelemetryInfo?.zoneName ?? "";

        this.appId = params.statTelemetryInfo?.appId ?? 0;
        StreamKitTelemetryHandler.setSessionData(
            this.subSessionId,
            this.sessionId,
            this.appId.toString(),
            this.isResume,
            this.zoneAddress
        );
        this.sleepDetector.setSessionId(this.sessionId);
        this.sleepDetector.setSubSessionId(this.subSessionId);

        // TODO: Should remove need for video element, for headless mode
        if (IsiOSVersionAtLeast(this.platformDetails, 15)) {
            const OLD_PLAY_PROP = "ragnarokOldPlay";
            const video = <any>this.videoElement;
            if (!video[OLD_PLAY_PROP]) {
                video[OLD_PLAY_PROP] = this.videoElement.play;
            }
            const shouldResetSrc =
                RagnarokSettings.allowSourceReset && IsiOSVersion(this.platformDetails, 15, 1);
            this.videoElement.play = () => {
                if (RagnarokSettings.allowAudioReset) {
                    Log.i("{0a9be04}", "{4a5b360}");
                    this.removeContextFix();
                    this.applyContextFix();
                    this.micCapturer.resetAudio();
                }
                if (shouldResetSrc) {
                    // WAR for blank video on app switch on iOS 15.1
                    Log.i("{0a9be04}", "{8f5cd77}");
                    const src = this.videoElement?.srcObject ?? null;
                    if (this.videoElement?.srcObject) {
                        this.videoElement.srcObject = null;
                        this.videoElement.srcObject = src;
                    }
                }
                return video[OLD_PLAY_PROP].apply(this.videoElement);
            };
        }
        // Workaround for bug where video freezes on iOS 15
        // We need to disable autoplay when initially launching a session to prevent the bug
        // The video is played when the user clicks continue in the UI
        // However, in the case of auto resume this dialog is not present so we need to let it autoplay
        if (
            RagnarokSettings.allowAutoplayChange &&
            (IsiOSVersion(this.platformDetails, 15, 0) || IsiOSVersion(this.platformDetails, 15, 1))
        ) {
            // using data prefix for custom tag: https://www.w3schools.com/tags/att_global_data.asp
            const kShouldAutoplay = "data-shouldautoplay";
            const kAutoplay = "autoplay";
            if (this.videoElement.hasAttribute(kShouldAutoplay)) {
                this.videoElement.setAttribute(kAutoplay, "");
            } else {
                this.videoElement.setAttribute(kShouldAutoplay, "");
                this.videoElement.removeAttribute(kAutoplay);
            }
        }

        // TODO: adjust StreamClient to not require remapping parameters
        let mediaConnectionInfo: ConnectionInfo = {
            ip: "",
            port: 0,
            appLevelProtocol: 0,
            usage: 0,
            protocol: 0
        };
        if (params.mediaConnectionInfo) {
            mediaConnectionInfo.ip = params.mediaConnectionInfo.address;
            mediaConnectionInfo.port = params.mediaConnectionInfo.port;
            // todo: remove, should be implied
            mediaConnectionInfo.usage = Usage.VIDEO;
        }

        const signalConnectionInfo: ServerInfo = {
            ip: params.signalConnectionInfo.address,
            port: params.signalConnectionInfo.port,
            protocol: params.signalConnectionInfo.secure ? "wss" : "ws"
        };

        const streamSettings = params.downstreamParams[0];
        const streamInfo: StreamInfo = {
            width: streamSettings.width,
            height: streamSettings.height,
            fps: streamSettings.fps,
            sdrHdrMode: streamSettings.sdrHdrMode
        };

        const session: GridSession = {
            appId: this.appId,
            appLaunchMode: params.enableTouchInput
                ? AppLaunchMode.TouchFriendly
                : AppLaunchMode.Default,
            clientLocale: params.statTelemetryInfo?.clientLocale ?? "",
            gpuType: params.statTelemetryInfo?.gpuType ?? "",
            mediaConnectionInfo: [mediaConnectionInfo],
            sessionId: this.sessionId,
            signalConnectionInfo: signalConnectionInfo,
            state: SessionState.READY_FOR_CONNECTION,
            streamInfo: [streamInfo],
            subSessionId: this.subSessionId,
            zoneAddress: this.zoneAddress,
            zoneName: this.zoneName,
            finalizedStreamingFeatures: params.streamingFeatures
        };

        const streamParam: StreamParam = {
            width: streamSettings.width,
            height: streamSettings.height,
            fps: streamSettings.fps,
            maxBitrateKbps: streamSettings.maxBitrateKbps,
            videoTagId: streamSettings.videoTagId,
            audioTagId: params.audioTagId
        };

        const inputFlags = { ...defaultInputConfigFlags };
        inputFlags.windowedStreaming ??= params.windowedStreaming;
        inputFlags.cursorType ??= params.cursorType;

        this.streamClient = new StreamClient(
            this,
            this.videoElement,
            this.audioElement,
            this.micCapturer,
            // todo: pass without exposing to client
            inputFlags,
            this.gamepadTester,
            this.gamepadHandler,
            StreamKitTelemetryHandler,
            this.platformDetails,
            session,
            streamParam,
            this.audioRecorder,
            this.initParams.textInputElement,
            params.statTelemetryInfo?.clientAppVersion,
            this.isResume,
            params.statTelemetryInfo?.region,
            streamSettings.dynamicStreamingMode,
            params.upstreamParams
        );

        this.registerWindowAndDocumentEvents();
        this.streamingState = StreamingState.STARTING_STREAM;
        this.sleepDetector.startSleepDetectionTimer();
        this.startTime = performance.now();

        this.streamClient.start();
        if (params.keyboardLayout) {
            this.streamClient.setKeyboardLayout(params.keyboardLayout);
        }

        return RErrorCode.Success;
    }

    public stopStreaming(reason?: number): void {
        if (!this.streamClient) {
            return;
        }
        if (
            this.streamingState !== StreamingState.STARTING_STREAM &&
            this.streamingState !== StreamingState.STREAMING
        ) {
            return;
        }

        reason = reason ?? RErrorCode.Success;

        Log.i("{0a9be04}", "{ae7f61b}", GetHexString(reason));

        this.streamingState = StreamingState.STOPPING_STREAM;
        this.shutdownClient(reason, true);
    }

    public getStreamingState(): StreamingState {
        return this.streamingState;
    }

    public sendCustomMessage(message: CustomMessage): boolean {
        this.streamClient?.sendCustomMessage(message);
        return !!this.streamClient;
    }

    // streaming property modification functions.
    public setStreamingMaxBitrate(kbps: number, index?: number, upstream?: boolean): boolean {
        this.streamClient?.setStreamingMaxBitrate(index ?? 0, kbps, upstream);
        return !!this.streamClient;
    }

    public setDynamicStreamingMode(
        mode: DynamicStreamingMode,
        index?: number,
        upstream?: boolean
    ): boolean {
        this.streamClient?.setDrcDfcState(index ?? 0, mode, upstream);
        return !!this.streamClient;
    }

    // input related functions
    public setKeyboardLayout(layout: string): boolean {
        this.streamClient?.setKeyboardLayout(layout);
        return !!this.streamClient;
    }

    // todo
    public setSupportedHidTypes(hidType: HidType): void {}

    public setGamepadRsdmm(enable: boolean): boolean {
        if (!this.streamClient) {
            return false;
        }
        this.gamepadHandler.toggleRsdmmMode(enable);
        return true;
    }

    public getVirtualInputController(): VirtualInputController {
        return this;
    }

    public getGamepadBitmap(): number {
        return this.gamepadHandler.getBitmap();
    }

    public captureUserInput(enable: boolean, inputs?: InputType): boolean {
        this.streamClient?.toggleUserInput(enable, inputs);
        return !!this.streamClient;
    }

    public sendTextInput(text: ArrayBuffer): boolean {
        this.streamClient?.sendTextInput(text);
        return !!this.streamClient;
    }

    // Mic related functions.
    public getMicState(): Promise<MicState> {
        return this.micCapturer.getCurrentMicStatus();
    }

    public captureMic(enable: boolean): boolean {
        if (!this.streamClient) {
            return false;
        }
        // TODO: Unify state handling method with streamclient
        // e.g. Move state handling entirely to lower layers
        // e.g. Only toggle if changed and only keep state in this layer
        this.micRecording = enable;
        this.micRecording
            ? this.micCapturer.startMicCaptureOnDefaultDeviceWithFallback()
            : this.micCapturer.stopMicRecording();
        return true;
    }

    // Debug Overlays:
    public displayDebugStats(display: boolean): boolean {
        if (!this.streamClient) {
            return false;
        }
        if (display !== this.debugStatsDisplayed) {
            this.debugStatsDisplayed = display;
            this.streamClient.toggleOnScreenStats(false, display);
        }
        return true;
    }

    public displayStutterIndicator(display: boolean): boolean {
        if (!this.streamClient) {
            return false;
        }
        if (display !== this.stutterIndicatorDisplayed) {
            this.stutterIndicatorDisplayed = display;
            this.streamClient.toggleStutterIndicator(display);
        }
        return true;
    }

    public setVideoTransforms(
        offsetX: number,
        offsetY: number,
        zoomFactor: number,
        streamIndex?: number
    ): boolean {
        this.streamClient?.setVideoTransforms(offsetX, offsetY, zoomFactor);
        return !!this.streamClient;
    }

    // Upstream source control
    public setUpstreamMedia(index: number, track: MediaStreamTrack): Promise<boolean> {
        if (!this.streamClient) {
            return Promise.resolve(false);
        }
        return this.streamClient?.setUpstreamMedia(index, track);
    }

    public unsetUpstreamMedia(index: number): Promise<boolean> {
        if (!this.streamClient) {
            return Promise.resolve(false);
        }
        return this.streamClient?.unsetUpstreamMedia(index);
    }

    public stopUpstreamMedia(index: number): Promise<boolean> {
        if (!this.streamClient) {
            return Promise.resolve(false);
        }
        return this.streamClient?.stopUpstreamMedia(index);
    }

    // IStreamCallbacks implementation
    public onSessionStart(data: StartSessionResultEvent): void {
        let startCode = RErrorCode.Success;
        if (data.error) {
            startCode = data.error.code;
            this.delegate.onStreamStartFailed(startCode);
            this.shutdownClient(startCode);
        } else if (data.streamInfo?.length && data.streams?.length) {
            this.streamingState = StreamingState.STREAMING;

            const audioOnlyStream = data.streams.find(stream => {
                const videoTrack = stream.tracks.find(track => track.kind === MediaTrackKind.VIDEO);
                const audioTrack = stream.tracks.find(track => track.kind === MediaTrackKind.AUDIO);
                return !videoTrack && audioTrack;
            });

            const startResult: StreamStartData = {
                streams: [],
                separateAudioStream: !!audioOnlyStream
            };

            for (const streamInfo of data.streamInfo) {
                const videoSettings: DownstreamVideoSettings = {
                    width: streamInfo.width,
                    height: streamInfo.height,
                    fps: streamInfo.fps,
                    sdrHdrMode: streamInfo.sdrHdrMode,
                    bitDepth: 8,
                    codec: this.streamClient?.getVideoCodec(),
                    videoTagId: this.videoElement?.tagName ?? ""
                };
                startResult.streams.push(videoSettings);
            }

            this.delegate.onStreamStarted(startResult);

            if (this.isResume && this.micRecording) {
                this.captureMic(true);
            }
        } else {
            startCode = RErrorCode.StreamerNoVideoTrack;
            Log.e("{0a9be04}", "{0cfb100}");
            this.delegate.onStreamStartFailed(startCode);
            this.shutdownClient(startCode);
        }

        StreamKitTelemetryHandler.sendStreamStartEvent(
            Math.round(performance.now() - this.startTime),
            GetHexString(startCode),
            this.streamClient?.getVideoCodec() ?? VideoCodecType.UNKNOWN
        );
        this.startTime = performance.now();
    }

    public onStreamStop(data: StreamingTerminatedEvent): void {
        this.shutdownClient(data.error.code);
    }

    public onUserIdleClear(): void {
        this.clearSystemIdleTimeout();
    }

    // todo: move to non-emitter pattern once refactor completes
    public emit(eventName: string, ...args: any[]): void {
        if (!args.length) {
            return;
        }
        switch (eventName) {
            case EVENTS.STREAMING_EVENT:
                this.delegate.onStreamWarning(args[0]);
                break;
            case EVENTS.STREAMING_QUALITY:
                this.delegate.onStreamQuality(args[0]);
                break;
            case EVENTS.MIC_CAPTURE:
                if (this.delegate.onMicStateUpdate && args[0].state !== undefined) {
                    this.delegate.onMicStateUpdate(args[0].state);
                }
                break;
            case EVENTS.TEXT_COMPOSITION:
                this.delegate.onTextComposition(args[0]);
                break;
            case EVENTS.CUSTOM_MESSAGE:
                if (this.delegate.onCustomMessage) {
                    this.delegate.onCustomMessage(args[0]);
                }
                break;
            case EVENTS.STREAM_STATS_UPDATE:
                this.delegate.onStreamStatistics(args[0]);
                break;
            default:
                break;
        }
    }

    public addListener(eventname: string, handler: Function): void {}

    public removeListener(eventname: string, handler: Function): void {}

    public removeAllListeners(): void {}

    public removeAllListenersOfEvent(eventname: string): void {}

    public hasListener(eventname: string): boolean {
        return true;
    }

    //VirtualInputController implementation
    public setClientSupportsVirtualGamepad(supported: boolean): void {
        this.gamepadHandler.getVirtualGamepadHandler().enable();
    }

    public handleVirtualGamepadState(
        buttons: number,
        trigger: number,
        axes: [number, number, number, number]
    ): void {
        this.gamepadHandler.getVirtualGamepadHandler().updateInput(buttons, trigger, axes);
    }

    public setVirtualKeyboardState(displayed: boolean): boolean {
        this.streamClient?.setVirtualKeyboardState(displayed);
        return !!this.streamClient;
    }

    public handleVirtualKeyInput(event: KeyboardEvent): boolean {
        this.streamClient?.sendKeyEvent(event);
        return !!this.streamClient;
    }

    // end of interfaces
    /**
     * Cleanup the state, listeners, etc. once a streaming session is ended.
     */
    private resetClient() {
        this.clearSystemIdleTimeout();

        this.streamClient = undefined;
        this.stutterIndicatorDisplayed = false;
        this.debugStatsDisplayed = false;
        this.startTime = 0;
        this.isResume = false;
        this.exitErrorCodeForUnload = undefined;
        this.visibilityHiddenTime = 0;
        this.deviceChangeCount = 0;
        this.sessionId = "";
        this.subSessionId = "";
        this.zoneAddress = "";
        this.zoneName = "";
        this.videoElement = undefined;
        this.audioElement = undefined;
        this.micRecording = false;
        this.appId = 0;

        // Note: we cannot call resetSessionData yet as our code is sending some events asynchronously.
        // Resetting data here will result in some metric events missing the sessionId and subSessionId.
        // StreamKitTelemetryHandler.resetSessionData();
        this.sleepDetector.setSessionId("");
        this.sleepDetector.setSubSessionId("");
    }

    /**
     * Collect startup timing related telemetry events, once per nskstreamclient.
     */
    private collectPerformanceTimingTelemetry() {
        if (RagnarokSettings.ragnarokConfig.sendNonEssentialMetricEvents) {
            const metricName = "PerformanceTiming";

            const jsInitEntry = performance.getEntriesByName("GfnJsInitStart")[0];
            const platformBeginEntry = performance.getEntriesByName("platformBegin")[0];
            const ragnarokBeginEntry = performance.getEntriesByName("GfnNskBegin")[0];
            const navigationEntries = performance.getEntriesByType("navigation");

            const jsInitTime = Math.round(jsInitEntry?.startTime ?? -1);
            const platformBeginTime = Math.round(platformBeginEntry?.startTime ?? -1);

            StreamKitTelemetryHandler.emitMetricEvent(
                metricName,
                "application timings",
                jsInitTime,
                platformBeginTime,
                Math.round(ragnarokBeginEntry?.startTime ?? -1),
                platformBeginTime - jsInitTime
            );

            if (!navigationEntries.length) {
                StreamKitTelemetryHandler.emitMetricEvent(
                    metricName,
                    "navigation timings unsupported",
                    0,
                    0,
                    0,
                    0
                );
                return;
            }

            const navigationEntry = navigationEntries[
                navigationEntries.length - 1
            ] as PerformanceNavigationTiming;

            StreamKitTelemetryHandler.emitMetricEvent(
                metricName,
                "load timings",
                Math.round(navigationEntry.loadEventStart),
                Math.round(navigationEntry.loadEventEnd),
                Math.round(navigationEntry.domContentLoadedEventStart),
                Math.round(navigationEntry.domContentLoadedEventEnd)
            );
        }
    }

    /**
     * Collect media capability related telemetry events, once per nskstreamclient.
     */
    private collectMediaCapabilitiesTelemetry() {
        const metricName = "MediaCapabilities";

        if (!IsMediaCapabilitiesSupported()) {
            StreamKitTelemetryHandler.emitMetricEvent(
                metricName,
                "API unsupported",
                -1,
                -1,
                -1,
                -1
            );
            return;
        }

        const startTime = performance.now();

        CapabilitiesHelper.getDecodeCapability("h264").then(
            async (baseCapability?: MediaCapabilitiesDecodingInfo) => {
                if (!baseCapability) {
                    StreamKitTelemetryHandler.emitMetricEvent(
                        metricName,
                        "webrtc unsupported",
                        performance.now() - startTime,
                        -1,
                        -1,
                        -1
                    );
                } else {
                    const highProfilePromise = CapabilitiesHelper.getDecodeCapability(
                        "h264; profile-level-id=640033", // 5.1 profile
                        2560,
                        1440,
                        60
                    );
                    const constrainedProfilePromise = CapabilitiesHelper.getDecodeCapability(
                        "h264; profile-level-id=64e033", // 5.1 constrained profile
                        2560,
                        1440,
                        60
                    );
                    const highProfileResult = ConvertCapabilityToNumber(await highProfilePromise);
                    const constrainedProfileResult = ConvertCapabilityToNumber(
                        await constrainedProfilePromise
                    );

                    const highProfileCheckTime = performance.now() - startTime;

                    const av1Promise = CapabilitiesHelper.getAv1Capabilities();
                    const highFpsPromise = CapabilitiesHelper.getDecodeCapability(
                        "h264",
                        1920,
                        1080,
                        120
                    );
                    const av1Result = ConvertCapabilityToNumber(await av1Promise);
                    const highFpsResult = ConvertCapabilityToNumber(await highFpsPromise);
                    const refreshRate = await CapabilitiesHelper.getRefreshRate();
                    const is120FpsSupported = await CapabilitiesHelper.is120FpsSupported();
                    Log.d("{0a9be04}", "{1efbc27}", refreshRate, is120FpsSupported);
                    StreamKitTelemetryHandler.emitMetricEvent(
                        "DisplayCaps",
                        "Is120FpsSupported: " + is120FpsSupported,
                        refreshRate,
                        0,
                        0,
                        0
                    );
                    StreamKitTelemetryHandler.emitMetricEvent(
                        metricName,
                        "codec 5.1: " +
                            highProfileResult +
                            " 5.1 constrained: " +
                            constrainedProfileResult +
                            " AV1: " +
                            av1Result,
                        highProfileCheckTime,
                        highFpsResult,
                        highProfileResult | constrainedProfileResult,
                        Math.round(window.screen.height)
                    );
                }
            }
        );
    }

    /**
     * Handle an exception that was not explicitly caught.
     * Emit an event, log, and potentially stop streaming.
     * @param unhandled Event that caused the unhandled exception
     * @returns False since we want the exception to propagate
     */
    private unhandledException = (unhandled: ErrorEvent): boolean => {
        Log.e("{0a9be04}", "{8516485}", unhandled.filename, unhandled.lineno, unhandled.colno);
        StreamKitTelemetryHandler.emitExceptionEvent(unhandled.error,
            unhandled.error && unhandled.error.message
                ? unhandled.error.message
                : unhandled.message,
            unhandled.filename,
            unhandled.lineno,
            unhandled.colno,
            false);

        if (RagnarokSettings.ragnarokConfig.terminateUnhandledException ?? true) {
            this.shutdownClient(RErrorCode.UnhandledException);
        }

        return false;
    };

    /**
     * Stop the stream client and clean up streaming
     * @param errorCode Code to stop the stream client with
     * @param clientInitiated Whether or not the end request was client initiated
     */
    private shutdownClient(errorCode: number, clientInitiated?: boolean) {
        if (
            this.streamingState === StreamingState.STOPPED ||
            this.streamingState === StreamingState.NONE
        ) {
            return;
        }
        const previousState = this.streamingState;
        this.streamingState = StreamingState.STOPPED;

        this.sleepDetector.stopSleepDetectionTimer();
        StreamKitTelemetryHandler.emitMetricEvent("HotPlug", "", 0, this.deviceChangeCount, 0, 0);
        if (IsiDevice(this.platformDetails) && this.micRecording) {
            Log.d("{0a9be04}", "{b9b1288}");
            this.captureMic(false);
        }
        this.micCapturer.shutdown();
        this.streamClient?.stop(errorCode);

        const wasSleepExit = this.sleepDetector.wasSleepExit(errorCode);
        if (wasSleepExit) {
            errorCode = ConvertErrorOnSleep(errorCode, this.platformDetails);
        }

        this.getConnectivityStatus(errorCode).then((connectivityStatus: ConnectivityStatus) => {
            errorCode = connectivityStatus.errorCode;

            if (previousState !== StreamingState.STARTING_STREAM) {
                // only send exit telemetry/notification if stream was successfully started
                // onSessionStart with an error will call the start failure callback and send the start telemetry
                let stopData: StreamStopData = {
                    code: errorCode,
                    isResumable: CanResume(errorCode, this.platformDetails),
                    reason: StreamStopReason.NETWORK_ERROR
                };
                if (clientInitiated) {
                    stopData.reason = StreamStopReason.CLIENT_DISCONNECTED;
                } else if ((errorCode & 0x0fffff00) === RErrorCode.StreamDisconnectedFromServer) {
                    // UEC middle 5 bytes determine the reporting module, outer bytes are for the severity and specific code
                    stopData.reason = StreamStopReason.SERVER_DISCONNECTED;
                }
                this.delegate.onStreamStopped(stopData);

                this.sendExitEvent(errorCode, connectivityStatus.connectivity);
            }

            this.removeContextFix();
            this.resetClient();
            this.unregisterWindowAndDocumentEvents();
        });
    }

    /**
     * Send a stream exit event
     * @param code Exit code associated with the exit. If not provided, will assume Success.
     * @param connectivity String that represents the connectivity status at the time of exit. @see ConnectivityStatus
     * @param sleep Whether or not the exit was related to the device going to sleep. If not provided, will assume false.
     */
    private sendExitEvent(code?: number, connectivity?: string, sleep?: boolean) {
        const result = code ? GetHexString(code) : GetHexString(RErrorCode.Success);
        let exitReason = result;
        if (sleep) {
            exitReason = "SleepExit";
        } else if (connectivity?.startsWith(Connectivity.ONLINE)) {
            exitReason = connectivity ?? "";
        }
        const duration = performance.now() - this.startTime;
        const codec = this.streamClient?.getVideoCodec() ?? VideoCodecType.UNKNOWN;
        const frameCount = this.streamClient?.getFramesDecoded() ?? 0;
        // todo: Add support for periodically caching of the stream exit telemetry events.
        StreamKitTelemetryHandler.sendStreamExitEvent(
            result,
            exitReason,
            duration,
            codec,
            frameCount
        );
    }

    /**
     * Handle the visibility change event.
     * Apply iOS specific audio and microphone WARs.
     * If we are losing visibility, cache an exit event because we might be shutdown soon.
     */
    private visibilityChange = () => {
        Log.i("{0a9be04}", "{478a408}", document.visibilityState, !!this.streamClient);
        if (!this.streamClient) {
            return;
        }
        const videoElement = this.videoElement;
        const audioElement = this.audioElement;
        // WAR for audio playing when device locked
        const shouldApplyMuteWAR =
            videoElement &&
            audioElement &&
            IsiOSVersionAtLeast(this.platformDetails, 15, 4) &&
            RagnarokSettings.allowMutingVideo;
        // WAR for mic disabled when PWA app minimized.  Do not apply when user terminated mic recorder.
        const shouldEnableMicWAR =
            IsiOSVersionAtLeast(this.platformDetails, 15, 4) &&
            this.micRecording &&
            RagnarokSettings.allowEnableMic;
        if (document.visibilityState === "hidden") {
            this.visibilityHiddenTime = performance.now();
            if (shouldApplyMuteWAR) {
                Log.i("{0a9be04}", "{cbd3b6e}");
                videoElement.muted = true;
                audioElement.muted = true;
            }
        } else if (document.visibilityState === "visible") {
            if (shouldApplyMuteWAR) {
                Log.i("{0a9be04}", "{05deefa}");
                videoElement.muted = false;
                audioElement.muted = false;
            }
            if (shouldEnableMicWAR) {
                // User terminated mic recorder if time between onended and visibility events above given threshold in milliseconds
                const USER_TERMINATED_MIC_RECORDER_THRESHOLD =
                    RagnarokSettings.ragnarokConfig.userTerminatedMicRecorderThreshold ?? 2000;
                if (
                    this.visibilityHiddenTime - this.micCapturer.getTrackEndedTime() <
                    USER_TERMINATED_MIC_RECORDER_THRESHOLD
                ) {
                    Log.i("{0a9be04}", "{862bd20}");
                    this.micCapturer.setWillAudioVolumeBeLow(true);
                    this.captureMic(true);
                } else {
                    this.micRecording = false;
                }
            } else if (
                IsiOSVersionAtLeast(this.platformDetails, 15, 4) &&
                !this.micCapturer.isAudioVolumeLow() &&
                this.micCapturer.isUsingBuiltInMic() &&
                this.micCapturer.didUserDisableMic()
            ) {
                this.micCapturer.recordLowAudioDebugEvent(LowAudioVolumeType.VISIBILITY_CHANGE);
            }
        }
    };

    /**
     * Handle page unload, by ending the stream gracefully.
     */
    private unload = () => {
        this.shutdownClient(
            this.exitErrorCodeForUnload ?? this.streamClient?.isStartNotified()
                ? RErrorCode.WebPageClosed
                : RErrorCode.SessionSetupCancelled
        );
    };

    /**
     * Handle and track audio device changes.
     */
    private deviceChange = () => {
        this.micCapturer.onDeviceChange(this.deviceChangeCount);
        this.deviceChangeCount++;
    };

    /**
     * Clear LG specific system idle timeout
     */
    private clearSystemIdleTimeout() {
        if (this.systemIdleId) {
            window.clearTimeout(this.systemIdleId);
            this.systemIdleId = 0;
        }
    }

    /**
     * Handle LG specific system idle timeout meant to prevent burn in
     * @param event LG specific system idle event
     */
    private handleSystemIdle = (event: LGSystemIdleEvent) => {
        // todo: eventually get from GES, likely come from client
        const DEFAULT_IDLE_SECONDS_LEFT = 60;
        if (event.detail.idle) {
            this.streamClient?.notifyIdleUpdate(
                RNotificationCode.ApproachingIdleTimeout,
                DEFAULT_IDLE_SECONDS_LEFT
            );
            this.systemIdleId = window.setTimeout(() => {
                this.streamClient?.stopStreamWithError(RErrorCode.ClientDisconnectedUserIdle);
            }, DEFAULT_IDLE_SECONDS_LEFT * 1000);
        }
    };

    /**
     * Register event listeners to monitor device while streaming.
     */
    private registerWindowAndDocumentEvents() {
        const zoneless = (window as any).zoneless as Zoneless;
        const windowAddEventListener =
            zoneless?.windowAddEventListener?.bind(window) ?? window.addEventListener.bind(window);

        const documentAddEventListener =
            zoneless?.documentAddEventListener?.bind(document) ??
            document.addEventListener.bind(document);

        // Using the `unload` event to determine terminate in page lifecycle it's unreliable, especially for iOS Safari.
        // Using `pagehide` event rather than `unload`, it will only be fired while closing tabs and refreshing pages.
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
        windowAddEventListener("pagehide", this.unload);
        windowAddEventListener("error", this.unhandledException);
        documentAddEventListener("visibilitychange", this.visibilityChange);
        if (navigator.mediaDevices) {
            navigator.mediaDevices.addEventListener("devicechange", this.deviceChange);
        }
        if (IsWebOS(this.platformDetails)) {
            documentAddEventListener("SystemIdle", this.handleSystemIdle);
        }
    }

    /**
     * Unregister event listeners that monitored the device while streaming.
     */
    private unregisterWindowAndDocumentEvents() {
        const zoneless = (window as any).zoneless as Zoneless;
        const windowRemoveEventListener =
            zoneless?.windowRemoveEventListener?.bind(window) ??
            window.removeEventListener.bind(window);
        const documentRemoveEventListener =
            zoneless?.documentRemoveEventListener?.bind(document) ??
            document.removeEventListener.bind(document);

        windowRemoveEventListener("pagehide", this.unload);
        windowRemoveEventListener("error", this.unhandledException);
        documentRemoveEventListener("visibilitychange", this.visibilityChange);
        if (navigator.mediaDevices) {
            navigator.mediaDevices.removeEventListener("devicechange", this.deviceChange);
        }
        if (IsWebOS(this.platformDetails)) {
            documentRemoveEventListener("SystemIdle", this.handleSystemIdle);
            this.clearSystemIdleTimeout();
        }
    }

    /**
     * Perform the WAR to reset audio.
     */
    private resetAudio = () => {
        this.micCapturer.resetAudio();
    };

    /**
     * Remove WAR to fix high latency audio issue on iOS 15
     */
    private removeContextFix() {
        if (!RagnarokSettings.allowAudioReset) {
            return;
        }
        if ((<any>window)[IOS_CONTEXT_PROP]) {
            (<any>window)[IOS_CONTEXT_PROP].close();
            (<any>window)[IOS_CONTEXT_PROP] = undefined;
            Log.i("{0a9be04}", "{0ac2a42}");
        }
        (<any>window)[IOS_STREAM_PROP] = undefined;
        if (navigator.mediaDevices && this.resetAudio) {
            navigator.mediaDevices.removeEventListener("devicechange", this.resetAudio);
        }
    }

    /**
     * Apply WAR to fix high latency audio issue on iOS 15
     */
    private applyContextFix() {
        if (!RagnarokSettings.allowAudioReset) {
            return;
        }
        if ((<any>window)[IOS_CONTEXT_PROP]) {
            Log.i("{0a9be04}", "{e2aba64}");
            return;
        }

        const AudioContext = (<any>window).AudioContext || (<any>window).webkitAudioContext;
        const context: AudioContext = new AudioContext(<AudioContextOptions>{
            latencyHint: "interactive",
            sampleRate: 48000
        });
        context.onstatechange = () => {
            if (context.state === "suspended") {
                context.resume();
                this.micCapturer.resetAudio();
                Log.i("{0a9be04}", "{faee684}");
            }
        };
        (<any>window)[IOS_CONTEXT_PROP] = context;
        (<any>window)[IOS_STREAM_PROP] = context.createMediaStreamDestination();

        if (navigator.mediaDevices && this.resetAudio) {
            navigator.mediaDevices.addEventListener("devicechange", this.resetAudio);
        }
        Log.i("{0a9be04}", "{b5f7b9b}");
    }

    /**
     * Determine whether or not the device currently has internet connectivity
     * @param errorCode Code that streaming was ended with
     * @returns Promise that resolves with the connectivity status
     */
    private getConnectivityStatus(errorCode?: number): Promise<ConnectivityStatus> {
        let connectivityStatus: ConnectivityStatus = {
            errorCode: errorCode ?? RErrorCode.Success,
            connectivity: Connectivity.ONLINE
        };

        if (errorCode && ShouldRunConnectivityTest(errorCode)) {
            let timeout = 1500;
            if (
                RagnarokSettings.ragnarokConfig.connectivityCheckTimeout &&
                RagnarokSettings.ragnarokConfig.connectivityCheckTimeout !== 0
            ) {
                timeout = RagnarokSettings.ragnarokConfig.connectivityCheckTimeout;
                Log.i("{0a9be04}", "{f9909d8}", timeout);
            }
            const connectivityUrl =
                (window.location.protocol === "http:" ? "http://" : "https://") + this.zoneAddress;
            Log.i("{0a9be04}", "{df38106}", connectivityUrl);
            const requestStartTime = performance.now();
            return customFetch(connectivityUrl, timeout, { method: "OPTIONS" })
                .then((response: FetchResponse) => {
                    if (
                        !(
                            (response.status >= 200 && response.status < 300) ||
                            response.status == 403
                        )
                    ) {
                        connectivityStatus.connectivity =
                            Connectivity.OFFLINE_WRONG_STATUS + "(" + String(response.status) + ")";
                    } else {
                        const requestCompleteTime =
                            Math.ceil((performance.now() - requestStartTime) / 25) * 25;
                        connectivityStatus.connectivity =
                            Connectivity.ONLINE + "(" + String(requestCompleteTime) + ")";
                    }
                    return connectivityStatus;
                })
                .catch(err => {
                    if (err.name === "AbortError") {
                        connectivityStatus.connectivity = Connectivity.TIMEOUT;
                    } else {
                        connectivityStatus.connectivity =
                            Connectivity.OFFLINE + "(" + err.name + ":" + err.message + ")";
                        connectivityStatus.errorCode = ConvertErrorOnConnectivityTest(errorCode);
                    }
                    return connectivityStatus;
                });
        } else {
            return Promise.resolve(connectivityStatus);
        }
    }

    /**
     * This function validates and updates session parameters.
     * @param params Parameters to validate and update.
     * @returns Validated and udpated stream parameters.
     * @note Will return an empty video element array or undefined audio element if they cannot be validated.
     */
    private updateStreamParams(params: StreamStartParameters): ValidatedStreamParams {
        let validated: ValidatedStreamParams = {
            streamParams: Object.assign({}, params),
            audioElement: undefined,
            videoElements: []
        };

        const audioElement = document.getElementById(params.audioTagId);
        if (audioElement && audioElement instanceof HTMLAudioElement) {
            validated.audioElement = audioElement;
        }
        for (const stream of validated.streamParams.downstreamParams) {
            const videoElement = document.getElementById(stream.videoTagId);
            if (videoElement && videoElement instanceof HTMLVideoElement) {
                validated.videoElements.push(videoElement);
            }
        }

        const shouldOverrideResolution =
            !!RagnarokSettings.resWidth && !!RagnarokSettings.resHeight;
        const shouldOverrideFps = !!RagnarokSettings.fps;
        if (shouldOverrideResolution || shouldOverrideFps) {
            validated.streamParams.downstreamParams?.forEach((item: DownstreamVideoSettings) => {
                if (shouldOverrideResolution) {
                    item.width = RagnarokSettings.resWidth;
                    item.height = RagnarokSettings.resHeight;
                }
                if (shouldOverrideFps) {
                    item.fps = RagnarokSettings.fps;
                }
            });

            if (shouldOverrideResolution) {
                Log.i("{0a9be04}", "{92a6b38}", RagnarokSettings.resWidth, RagnarokSettings.resHeight);
            }
            if (shouldOverrideFps) {
                Log.d("{0a9be04}", "{6618bcd}", RagnarokSettings.fps);
            }
        }

        return validated;
    }
}
